import type { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import axios from 'axios';
import { debounce } from 'lodash';

import { authenticateSmsOtp, authenticateEmailOtp } from '~/api/onboarding';
import { history } from '~/index';
import { API_URL, TENANT, API_SOLUTION } from '~/env';
import i18n from '~/i18n';
import { verifyToken } from './auth';

interface AuthConstructor {
  tokenHeaderKey: string;
  tokenLocalStorageKey: string;
  profileHeaderKey: string;
  profileLocalStorageKey: string;
  solutionLocalStorageKey: string;
}

export type AuthMethod = 'smsOtp' | 'emailOtp' | 'password';

class Auth {
  public tokenHeaderKey: string;

  private tokenLocalStorageKey: string;

  public profileHeaderKey: string;

  public profileLocalStorageKey: string;

  public solutionLocalStorageKey: string;

  constructor({
    tokenHeaderKey,
    tokenLocalStorageKey,
    profileHeaderKey,
    profileLocalStorageKey,
    solutionLocalStorageKey,
  }: AuthConstructor) {
    this.tokenHeaderKey = tokenHeaderKey;
    this.tokenLocalStorageKey = tokenLocalStorageKey;
    this.profileHeaderKey = profileHeaderKey;
    this.profileLocalStorageKey = profileLocalStorageKey;
    this.solutionLocalStorageKey = solutionLocalStorageKey;
  }

  public authMethod = 'authMethod';

  setToken(headers: any, authMethod: AuthMethod) {
    localStorage.setItem(this.tokenLocalStorageKey, headers[this.tokenHeaderKey]);
    localStorage.setItem(this.authMethod, authMethod);
    window.dispatchEvent(new Event('local-storage'));
  }

  getToken() {
    return localStorage.getItem(this.tokenLocalStorageKey);
  }

  getAuthMethod(): AuthMethod | undefined {
    return localStorage.getItem(this.authMethod) as AuthMethod | undefined;
  }

  deleteToken() {
    localStorage.removeItem(this.tokenLocalStorageKey);
    localStorage.removeItem(this.profileLocalStorageKey);
    localStorage.removeItem(this.solutionLocalStorageKey);
  }

  setProfile(profile: string) {
    window.dispatchEvent(new Event('local-storage'));
    localStorage.setItem(this.profileLocalStorageKey, profile);
  }

  setSolution(solutionId: string) {
    window.dispatchEvent(new Event('local-storage'));
    localStorage.setItem(this.solutionLocalStorageKey, solutionId);
  }

  getProfile() {
    return localStorage.getItem(this.profileLocalStorageKey);
  }

  getSolution() {
    return localStorage.getItem(this.solutionLocalStorageKey);
  }
}

export const auth = new Auth({
  tokenHeaderKey: 'v-access-token',
  tokenLocalStorageKey: 'token',
  profileHeaderKey: 'v-profile',
  profileLocalStorageKey: 'profile',
  solutionLocalStorageKey: 'solutionId',
});

const instance: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'v-tenant': TENANT,
    'v-solution': API_SOLUTION,
    'v-api-channel': 'WEB',
    'v-version': '3.0.0',
  },
  paramsSerializer: (params: Record<string, any>) => {
    const parts: string[] = [];
    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((val) => {
          if (val !== undefined && val !== null) {
            parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(val)}`);
          }
        });
      } else if (value !== undefined && value !== null) {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      }
    });
    return parts.join('&');
  },
});

instance.interceptors.request.use((config: AxiosRequestConfig): AxiosRequestConfig => {
  const result = config;
  const token = auth.getToken();
  const profile = auth.getProfile();
  const solutionId = auth.getSolution();
  if (token) {
    result.headers[auth.tokenHeaderKey] = token;
  }
  if (profile) {
    result.headers[auth.profileHeaderKey] = profile;
  }
  if (solutionId) {
    result.headers['v-solution'] = solutionId;
  }

  const fullLocale = i18n?.language;
  const localeWithoutRegion = fullLocale.slice(0, 2);
  if (fullLocale) {
    result.headers[
      'Accept-Language'
    ] = `${fullLocale}, ${localeWithoutRegion};q=0.9, en;q=0.8, *;q=0.5`;
  }

  return result;
});

instance.interceptors.response.use(
  (response) => {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    return response;
  },
  (error: AxiosError) => {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error
    console.log(`error`);
    if (error.response?.status === 401) {
      switch (error.config.url) {
        case authenticateSmsOtp.url:
        case verifyToken.url:
        case authenticateEmailOtp.url:
          console.log(`break`);
          break;
        default: {
          const prevPagePath = history.location.pathname;
          void goToLoginPage(prevPagePath);
        }
      }
    }
    return Promise.reject(error);
  }
);

const goToLoginPage = debounce(async (prevPagePath: string) => {
  try {
    await verifyToken();
  } catch (error: any) {
    auth.deleteToken();
    pushToLogin(prevPagePath);
  }
}, 10);

export const pushToLogin = (path?: string): void => {
  const currPath = history.location.pathname;
  const prevPagePath = path || currPath;
  if (
    !['/login', '/invitation', '/onboarding/personalInfo/authenticate'].find(
      (value) => value === prevPagePath
    )
  ) {
    history.push('/login', { prevPagePath });
  }
};

export type ResponseType<R = any> = Promise<AxiosResponse<R>>;

export default instance;
